// Especificación SERIALIZABLE de un documento firmado (2026-09-12) — lo que el
// navegador manda a `POST /api/hce/firmar-documento` en vez de un PDF ya
// armado. Deliberadamente NO lleva nombre, registro ni fecha del profesional:
// esos los pone el servidor (`FirmaPdf`) desde la sesión, así el bloque del
// firmante (y el QR de la receta, que los incluye) nunca depende del
// navegador. El contenido clínico sí viene del navegador: es lo que el médico
// escribió.
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import type { DatosContactoConsultorio } from './datosConsultorioHeader';
import type { DatosEncabezadoMsp } from './encabezadoMsp';
import type { DatosPacienteFemo } from './femo';
import type { FuentesPdf } from './fuentes';
import { generarPdfActaEntrega } from './acta';
import { generarPdfFemoCertificado, generarPdfFemoExpediente } from './femo';
import { generarPdfFormulario, generarPdfFormularioDePares, generarPdfFormularioMsp } from './formularioGenerico';
import { sustituirMarcaFirmante } from './firmanteServidor';
import type { DatosDenverPdf } from './denverII';
import type { DatosOdontogramaPdf } from './odontograma';
import type { DatosPartogramaPdf } from './partograma';
import { CLAVES_SILUETA_VALIDAS, type DatosSiluetaPdf } from './siluetaCorporal';
import type { DatosGraficoConstantesUciPdf } from './graficoConstantesUci';
import { COLORES_SERIE_VALIDOS, ESTILOS_CURVA_VALIDOS, type DatosCurvasCrecimientoPdf } from './curvasCrecimiento';
import type { DatosSignosVitalesPdf } from './signosVitalesTimeline';
import { generarPdfReceta, type DatosPdfReceta } from './receta';

export interface PacientePdf {
  nombre: string;
  identificador?: string;
}

export interface ParPdf {
  pregunta: string;
  respuesta: string;
}

/** Lo que el servidor decide y el navegador nunca manda. */
export interface FirmaPdf {
  practitionerNombre: string;
  /** Matrícula del profesional — o la CÉDULA en 117/116-cert, decidido por el servidor según `tipoDocumento`. */
  practitionerRegistro: string;
  fechaISO: string;
}

export type DatosRecetaSinFirma = Omit<DatosPdfReceta, 'practitionerNombre' | 'practitionerRegistro' | 'fechaISO' | 'urlValidacion'>;

export type EspecificacionPdf =
  | { modo: 'questionnaire'; questionnaire: Questionnaire; response: QuestionnaireResponse; paciente: PacientePdf; fuenteFormulario?: string }
  /** `curvasCrecimiento` — 028A-1/028B/028C (curvas Fenton y OMS, sin encabezado MSP): mismas gráficas que en modo 'msp' (ver `curvasCrecimiento.ts`). */
  | { modo: 'pares'; titulo: string; pares: ParPdf[]; paciente: PacientePdf; curvasCrecimiento?: DatosCurvasCrecimientoPdf; fuenteFormulario?: string }
  /**
   * `diagramaOdontograma` — solo 033: grilla FDI dispersa (ver `odontograma.ts`), acotada por `LIMITES_ESPECIFICACION.piezasOdontograma`.
   * `diagramaDenver` — solo 028 (Test de Denver II): resumen de resultados por ítem evaluado (ver `denverII.ts`).
   * `diagramaSignosVitales` — 018A y 019 (Fase 1B): gráfico del registro transanestésico/monitorización, mismo tipo compartido por ambos (ver `signosVitalesTimeline.ts`).
   * `diagramaPartograma` — solo 051 (Fase 4): gráfico dilatación×horas + curva de alerta p10 + tabla horaria (ver `partograma.ts`).
   * `siluetaCorporal` — solo 003 (Fase 7): regiones marcadas en las 4 vistas de la silueta (ver `siluetaCorporal.ts`).
   * `curvasCrecimiento` — 056A/056B: gráficas de Peso/Edad, Talla/Edad, IMC/Edad y Velocidad de crecimiento, con la referencia y las mediciones del paciente (ver `curvasCrecimiento.ts`).
   * `diagramasGraficoConstantesUci` — 113/114/115 (Fase 4): gráfico(s) de constantes superpuestas, escala propia por serie (ver `graficoConstantesUci.ts`). Un elemento por CAJA impresa — 113 tiene 1 (FR/T°/Pulso/TA comparten una sola grilla), 114 tiene 2 (temperatura aparte, PA/pulso/FR en otra).
   */
  | {
      modo: 'msp';
      pares: ParPdf[];
      datosEncabezadoMsp: DatosEncabezadoMsp;
      diagramaOdontograma?: DatosOdontogramaPdf;
      diagramaDenver?: DatosDenverPdf;
      diagramaSignosVitales?: DatosSignosVitalesPdf;
      diagramaPartograma?: DatosPartogramaPdf;
      diagramasGraficoConstantesUci?: readonly DatosGraficoConstantesUciPdf[];
      siluetaCorporal?: DatosSiluetaPdf;
      curvasCrecimiento?: DatosCurvasCrecimientoPdf;
    }
  | { modo: 'femo-expediente' | 'femo-certificado'; response: QuestionnaireResponse; paciente: DatosPacienteFemo; huboFirmaTrabajador: boolean }
  /** `qrToken` ausente = receta especial (sustancia controlada), sin QR propio. */
  | { modo: 'receta'; datos: DatosRecetaSinFirma; qrToken?: string }
  /**
   * Acta de Entrega-Recepción (Modo Consulta, ADR-EMR-4). Sin contenido del
   * navegador: `consultorio` lo completa SIEMPRE el servidor desde el tenant
   * de la sesión — `validarEspecificacionPdf` descarta el que mande el cliente.
   */
  | { modo: 'acta-entrega'; consultorio?: { nombre: string; slug: string } };

export interface OpcionesRender {
  /** Solo receta: URL completa del portal de validación con el fragmento del QR, ya armada por el servidor. */
  urlValidacion?: string;
  /**
   * Dirección/teléfono/correo del consultorio (pedido de Sue, 2026-09-14) —
   * resuelto SIEMPRE por el servidor desde el tenant de la sesión (nunca del
   * navegador), igual que `FirmaPdf`. Receta y acta-entrega ya traen los
   * suyos por su propio camino (`datos`/`consultorio`); este campo alimenta
   * los modos que no tenían ninguno: questionnaire/pares/msp/femo-*.
   */
  contacto?: DatosContactoConsultorio;
}

/**
 * Único punto de entrada del servidor. En la receta, `firma` se aplica
 * DESPUÉS de `datos`: aunque el navegador colara un `practitionerNombre` en
 * `datos`, el del servidor lo pisa.
 */
export async function renderizarPdf(espec: EspecificacionPdf, firma: FirmaPdf, fuentes: FuentesPdf, opciones: OpcionesRender = {}): Promise<Uint8Array> {
  switch (espec.modo) {
    case 'questionnaire':
      return generarPdfFormulario(espec.questionnaire, espec.response, { ...datosFormulario(espec.paciente, firma, opciones.contacto), fuenteFormulario: espec.fuenteFormulario }, fuentes);
    case 'pares':
      return generarPdfFormularioDePares(espec.titulo, espec.pares, { ...datosFormulario(espec.paciente, firma, opciones.contacto), fuenteFormulario: espec.fuenteFormulario }, fuentes, espec.curvasCrecimiento);
    case 'msp':
      return generarPdfFormularioMsp(
        sustituirMarcaFirmante(espec.pares, firma, espec.datosEncabezadoMsp.etiquetaIdentificacion),
        { ...espec.datosEncabezadoMsp, ...camposContactoConsultorio(opciones.contacto) },
        firma,
        fuentes,
        espec.diagramaOdontograma,
        espec.diagramaDenver,
        espec.diagramaSignosVitales,
        espec.diagramaPartograma,
        espec.diagramasGraficoConstantesUci,
        espec.siluetaCorporal,
        espec.curvasCrecimiento,
      );
    case 'femo-expediente':
      return generarPdfFemoExpediente(espec.response, espec.paciente, firma, espec.huboFirmaTrabajador, fuentes, opciones.contacto);
    case 'femo-certificado':
      return generarPdfFemoCertificado(espec.response, espec.paciente, firma, espec.huboFirmaTrabajador, fuentes, opciones.contacto);
    case 'receta':
      return generarPdfReceta(datosRecetaCompletos(espec.datos, firma, opciones.urlValidacion), fuentes);
    case 'acta-entrega':
      if (!espec.consultorio) throw new Error('acta-entrega sin consultorio: lo completa el servidor antes de renderizar.');
      return generarPdfActaEntrega(
        { clinicName: espec.consultorio.nombre, tenantSlug: espec.consultorio.slug, ...firma, ...camposContactoConsultorio(opciones.contacto) },
        fuentes,
      );
  }
}

/** `direccionConsultorio`/`telefonoConsultorio`/`correoConsultorio` — mismos nombres de campo en `DatosEncabezadoMsp` y `DatosPdfActaEntrega`/`DatosPdfFormulario`. */
function camposContactoConsultorio(contacto: DatosContactoConsultorio | undefined) {
  return { direccionConsultorio: contacto?.direccion, telefonoConsultorio: contacto?.telefono, correoConsultorio: contacto?.correo };
}

/** Datos completos de la receta (PDF y QR) — firma del servidor siempre por encima de lo que traiga `datos`. */
export function datosRecetaCompletos(datos: DatosRecetaSinFirma, firma: FirmaPdf, urlValidacion?: string): DatosPdfReceta {
  return { ...datos, practitionerNombre: firma.practitionerNombre, practitionerRegistro: firma.practitionerRegistro, fechaISO: firma.fechaISO, urlValidacion };
}

function datosFormulario(paciente: PacientePdf, firma: FirmaPdf, contacto: DatosContactoConsultorio | undefined) {
  return { pacienteNombre: paciente.nombre, pacienteIdentificador: paciente.identificador, ...firma, ...camposContactoConsultorio(contacto) };
}

// ----------------------------------------------------------------------------
// Validación de forma en tiempo de ejecución: el cuerpo llega del navegador y
// no es de fiar. Solo chequea lo que los generadores necesitan para no
// romperse (no re-valida el contenido clínico).
// ----------------------------------------------------------------------------

function esObjeto(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function esTexto(v: unknown): v is string {
  return typeof v === 'string';
}

function esTextoOpcional(v: unknown): boolean {
  return v === undefined || typeof v === 'string';
}

/**
 * Topes de tamaño: el PDF se renderiza SÍNCRONO en el único proceso que
 * atiende a todos los tenants — una especificación gigante lo bloquearía
 * (hallazgo del revisor, 2026-09-12). Referencia real: el Questionnaire más
 * grande del repo (FEMO.json) tiene 528 nodos, profundidad 8 y su texto más
 * largo 218 caracteres — los topes dejan margen amplio para Questionnaire +
 * Response + notas clínicas libres largas.
 */
export const LIMITES_ESPECIFICACION = {
  nodos: 20_000,
  profundidad: 30,
  largoTexto: 50_000,
  pares: 1_000,
  medicamentos: 30,
  piezasOdontograma: 60,
  itemsDenver: 120,
  // 9 h (018A) a un punto cada 5 min x 7 parametros = 756; margen hasta 1000.
  puntosSignosVitales: 1_000,
  eventosSignosVitales: 20,
  puntosPartograma: 60,
  filasHorariasPartograma: 16,
  eventosPartograma: 60,
  dipsPartograma: 120,
  seriesGraficoConstantesUci: 8,
  puntosPorSerieGraficoConstantesUci: 24,
  diagramasGraficoConstantesUci: 3,
  graficasCurvasCrecimiento: 6,
  seriesPorGraficaCurva: 12,
  puntosPorSerieCurva: 80,
} as const;

/** Recorrido iterativo (no recursivo: una profundidad maliciosa no debe desbordar la pila). */
function dentroDeLimites(raiz: unknown): boolean {
  let nodos = 0;
  const pendientes: [unknown, number][] = [[raiz, 0]];
  while (pendientes.length > 0) {
    const [valor, profundidad] = pendientes.pop()!;
    nodos++;
    if (nodos + pendientes.length > LIMITES_ESPECIFICACION.nodos || profundidad > LIMITES_ESPECIFICACION.profundidad) return false;
    if (typeof valor === 'string') {
      if (valor.length > LIMITES_ESPECIFICACION.largoTexto) return false;
    } else if (typeof valor === 'object' && valor !== null) {
      for (const hijo of Array.isArray(valor) ? valor : Object.values(valor)) pendientes.push([hijo, profundidad + 1]);
    }
  }
  return true;
}

function paresValidos(v: unknown): v is ParPdf[] {
  return Array.isArray(v) && v.length <= LIMITES_ESPECIFICACION.pares && v.every((p) => esObjeto(p) && esTexto(p.pregunta) && esTexto(p.respuesta));
}

function pacienteValido(v: unknown): v is PacientePdf {
  return esObjeto(v) && esTexto(v.nombre) && esTextoOpcional(v.identificador);
}

/** Recesión/movilidad: entero 1-4 (la captura ofrece 1-4 y 1-3). Evita que un valor absurdo desborde la cajita del PDF firmado. */
function gradoValido(v: unknown): boolean {
  return v === undefined || (typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 4);
}

/** Solo valida forma/tope — igual que `paresValidos`, no revalida si `hallazgo` es un código conocido de la leyenda (`dibujarOdontograma` ya ignora cualquier código que no reconozca). */
function diagramaOdontogramaValido(v: unknown): v is DatosOdontogramaPdf | undefined {
  if (v === undefined) return true;
  return (
    Array.isArray(v) &&
    v.length <= LIMITES_ESPECIFICACION.piezasOdontograma &&
    v.every(
      (e) =>
        esObjeto(e) &&
        esTexto(e.pieza) &&
        esTextoOpcional(e.hallazgo) &&
        gradoValido(e.recesion) &&
        gradoValido(e.movilidad) &&
        (e.superficies === undefined || (esObjeto(e.superficies) && Object.keys(e.superficies).length <= 5)),
    )
  );
}

const RESULTADOS_DENVER = new Set(['aprobado', 'fallado', 'rehuso', 'sin-oportunidad']);
const CLASIFICACIONES_DENVER_VALIDAS = new Set(['normal', 'dudoso', 'anormal']);

/** Solo valida forma/tope, igual que `diagramaOdontogramaValido` — no revalida si `itemId` es un ítem real del catálogo (`dibujarDenver` ya ignora cualquier id que no reconozca). */
function diagramaDenverValido(v: unknown): v is DatosDenverPdf | undefined {
  if (v === undefined) return true;
  if (!esObjeto(v) || !Array.isArray(v.entradas) || v.entradas.length > LIMITES_ESPECIFICACION.itemsDenver) return false;
  const entradasOk = v.entradas.every((e) => esObjeto(e) && esTexto(e.itemId) && typeof e.resultado === 'string' && RESULTADOS_DENVER.has(e.resultado));
  const edadOk = v.edadEvaluacionMeses === undefined || typeof v.edadEvaluacionMeses === 'number';
  const gestacionalOk = v.edadGestacionalSemanas === undefined || (typeof v.edadGestacionalSemanas === 'number' && Number.isFinite(v.edadGestacionalSemanas));
  const clasificacionOk = v.clasificacionPresuntiva === undefined || (typeof v.clasificacionPresuntiva === 'string' && CLASIFICACIONES_DENVER_VALIDAS.has(v.clasificacionPresuntiva));
  return entradasOk && edadOk && gestacionalOk && clasificacionOk;
}

const PARAMETROS_SIGNOS_VITALES_VALIDOS = new Set(['tas', 'tad', 'tam', 'fc', 'temperatura', 'pvc', 'sato2']);
const EVENTOS_ANESTESIA_VALIDOS = new Set(['inicio-anestesia', 'induccion', 'inicio-cirugia', 'fin-cirugia', 'fin-anestesia']);

/** Solo valida forma/tope, igual que `diagramaOdontogramaValido`/`diagramaDenverValido` — no revalida si el minuto es clínicamente razonable (`dibujarSignosVitales` ya acota fuera de [0, `MINUTO_MAX_SIGNOS_VITALES`]). */
function diagramaSignosVitalesValido(v: unknown): v is DatosSignosVitalesPdf | undefined {
  if (v === undefined) return true;
  if (!esObjeto(v) || !Array.isArray(v.puntos) || !Array.isArray(v.eventos)) return false;
  if (v.puntos.length > LIMITES_ESPECIFICACION.puntosSignosVitales || v.eventos.length > LIMITES_ESPECIFICACION.eventosSignosVitales) return false;
  const puntosOk = v.puntos.every((p) => esObjeto(p) && typeof p.parametro === 'string' && PARAMETROS_SIGNOS_VITALES_VALIDOS.has(p.parametro) && typeof p.minuto === 'number' && typeof p.valor === 'number');
  const eventosOk = v.eventos.every((e) => esObjeto(e) && typeof e.tipo === 'string' && EVENTOS_ANESTESIA_VALIDOS.has(e.tipo) && typeof e.minuto === 'number');
  return puntosOk && eventosOk;
}

const PARIDAD_MEMBRANA_VALIDAS = new Set(['todas-integras', 'multiparas-integras', 'multiparas-rotas', 'nuliparas-integras', 'nuliparas-rotas']);
const PLANOS_HODGE_VALIDOS = new Set<string>(['I', 'II', 'III', 'IV']);
const TIPOS_DIP_VALIDOS = new Set<string>(['I', 'II', 'V', 'M']);
const COLUMNAS_HORARIAS_VALIDAS = new Set(['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p']);

/** Solo valida forma/tope, igual que `diagramaOdontogramaValido`/`diagramaDenverValido` — no revalida si los valores de la tabla horaria son clínicamente razonables (`dibujarPartograma` ya recorta la hora/dilatación fuera de rango). */
function diagramaPartogramaValido(v: unknown): v is DatosPartogramaPdf | undefined {
  if (v === undefined) return true;
  if (!esObjeto(v) || !Array.isArray(v.puntos) || !Array.isArray(v.filasHorarias)) return false;
  if (v.puntos.length > LIMITES_ESPECIFICACION.puntosPartograma || v.filasHorarias.length > LIMITES_ESPECIFICACION.filasHorariasPartograma) return false;
  const puntosOk = v.puntos.every((p) => esObjeto(p) && typeof p.hora === 'number' && typeof p.dilatacion === 'number');
  const filasOk = v.filasHorarias.every((f) => esObjeto(f) && esTexto(f.columna) && COLUMNAS_HORARIAS_VALIDAS.has(f.columna));
  const horaBaseOk = v.horaBaseLineaAlerta === undefined || typeof v.horaBaseLineaAlerta === 'number';
  const enRango = (n: unknown, min: number, max: number): boolean => typeof n === 'number' && Number.isFinite(n) && n >= min && n <= max;
  const listaOk = (l: unknown, tope: number, valida: (x: Record<string, unknown>) => boolean): boolean =>
    l === undefined || (Array.isArray(l) && l.length <= tope && l.every((x) => esObjeto(x) && valida(x)));
  const fcfOk = listaOk(v.fcf, LIMITES_ESPECIFICACION.eventosPartograma, (x) => enRango(x.hora, 0, 14) && enRango(x.fcf, 30, 300));
  const hodgeOk = listaOk(v.hodge, LIMITES_ESPECIFICACION.eventosPartograma, (x) => enRango(x.hora, 0, 14) && esTexto(x.plano) && PLANOS_HODGE_VALIDOS.has(x.plano) && (x.variedad === undefined || (esTexto(x.variedad) && x.variedad.length <= 20)));
  const dipsOk = listaOk(v.dips, LIMITES_ESPECIFICACION.dipsPartograma, (x) => enRango(x.hora, 0, 14) && esTexto(x.tipo) && TIPOS_DIP_VALIDOS.has(x.tipo));
  const remRamOk = (v.rem === undefined || enRango(v.rem, 0, 14)) && (v.ram === undefined || enRango(v.ram, 0, 14));
  const paridadMembranaOk = (v.paridad === undefined && v.membranas === undefined) || (esTexto(v.paridad) && esTexto(v.membranas) && PARIDAD_MEMBRANA_VALIDAS.has(`${v.paridad}-${v.membranas}`));
  return puntosOk && filasOk && horaBaseOk && paridadMembranaOk && fcfOk && hodgeOk && dipsOk && remRamOk;
}

const SIMBOLOS_GRAFICO_CONSTANTES_VALIDOS = new Set(['circulo', 'circuloHueco', 'triangulo', 'cruz', 'cuadrado']);

/** Solo valida forma/tope, igual que `diagramaPartogramaValido` — no revalida si la columna horaria es una de las 24 reales del turno (`dibujarGraficoConstantesUci` ya ignora cualquier columna que no reconozca al ordenar). */
function diagramaGraficoConstantesUciValido(v: unknown): v is DatosGraficoConstantesUciPdf {
  if (!esObjeto(v) || !Array.isArray(v.series) || v.series.length > LIMITES_ESPECIFICACION.seriesGraficoConstantesUci) return false;
  return v.series.every((s) => {
    if (!esObjeto(s) || !esTexto(s.clave) || !esTexto(s.etiqueta) || typeof s.escalaMin !== 'number' || typeof s.escalaMax !== 'number') return false;
    if (!esTexto(s.simbolo) || !SIMBOLOS_GRAFICO_CONSTANTES_VALIDOS.has(s.simbolo)) return false;
    if (s.variantes !== undefined && !(Array.isArray(s.variantes) && s.variantes.every((va) => esObjeto(va) && esTexto(va.clave) && esTexto(va.simbolo) && SIMBOLOS_GRAFICO_CONSTANTES_VALIDOS.has(va.simbolo)))) return false;
    return Array.isArray(s.puntos) && s.puntos.length <= LIMITES_ESPECIFICACION.puntosPorSerieGraficoConstantesUci && s.puntos.every((p) => esObjeto(p) && esTexto(p.columna) && typeof p.valor === 'number' && esTextoOpcional(p.variante));
  });
}

/** Un elemento por caja impresa (113: 1, 114: 2) — ver el comentario de `diagramasGraficoConstantesUci` en `EspecificacionPdf`. */
function diagramasGraficoConstantesUciValidos(v: unknown): v is readonly DatosGraficoConstantesUciPdf[] | undefined {
  if (v === undefined) return true;
  return Array.isArray(v) && v.length <= LIMITES_ESPECIFICACION.diagramasGraficoConstantesUci && v.every(diagramaGraficoConstantesUciValido);
}

/** Solo claves `<vista>:<región>` del catálogo, sin repetir y acotadas. */
function siluetaCorporalValida(v: unknown): v is DatosSiluetaPdf | undefined {
  if (v === undefined) return true;
  if (!esObjeto(v) || !Array.isArray(v.regiones) || v.regiones.length > CLAVES_SILUETA_VALIDAS.size) return false;
  return v.regiones.every((r) => typeof r === 'string' && CLAVES_SILUETA_VALIDAS.has(r)) && new Set(v.regiones).size === v.regiones.length;
}

/** Solo forma y topes (números finitos, estilos conocidos): lo que dibuja `dibujarCurva` no depende de más. */
function curvasCrecimientoValidas(v: unknown): v is DatosCurvasCrecimientoPdf | undefined {
  if (v === undefined) return true;
  if (!esObjeto(v) || !Array.isArray(v.graficas) || v.graficas.length > LIMITES_ESPECIFICACION.graficasCurvasCrecimiento) return false;
  const finito = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
  // Ejes acotados: con magnitudes enormes o rangos minúsculos el paso de las marcas no avanzaría (o la escala daría Infinity/NaN).
  const ejeValido = (min: unknown, max: unknown): boolean => finito(min) && finito(max) && Math.abs(min) <= 1e6 && Math.abs(max) <= 1e6 && max - min >= 1e-3;
  return v.graficas.every(
    (g) =>
      esObjeto(g) &&
      esTexto(g.titulo) &&
      esTextoOpcional(g.nota) &&
      esTexto(g.ejeXEtiqueta) &&
      esTexto(g.ejeYEtiqueta) &&
      ejeValido(g.xMin, g.xMax) &&
      ejeValido(g.yMin, g.yMax) &&
      (g.pasoX === undefined || (finito(g.pasoX) && g.pasoX > 0 && ((g.xMax as number) - (g.xMin as number)) / g.pasoX <= 100)) &&
      (g.ejeYSecundario === undefined ||
        (esObjeto(g.ejeYSecundario) && esTexto(g.ejeYSecundario.etiqueta) && ejeValido(g.ejeYSecundario.yMin, g.ejeYSecundario.yMax) && (g.ejeYSecundario.paso === undefined || (finito(g.ejeYSecundario.paso) && g.ejeYSecundario.paso > 0 && ((g.ejeYSecundario.yMax as number) - (g.ejeYSecundario.yMin as number)) / g.ejeYSecundario.paso <= 100)) && (g.ejeYSecundario.color === undefined || (typeof g.ejeYSecundario.color === 'string' && COLORES_SERIE_VALIDOS.has(g.ejeYSecundario.color))))) &&
      Array.isArray(g.series) &&
      g.series.length <= LIMITES_ESPECIFICACION.seriesPorGraficaCurva &&
      g.series.every(
        (s) =>
          esObjeto(s) &&
          esTexto(s.etiqueta) &&
          esTexto(s.estilo) &&
          ESTILOS_CURVA_VALIDOS.has(s.estilo) &&
          (s.color === undefined || (typeof s.color === 'string' && COLORES_SERIE_VALIDOS.has(s.color))) &&
          (s.ejeSecundario === undefined || (typeof s.ejeSecundario === 'boolean' && (!s.ejeSecundario || g.ejeYSecundario !== undefined))) &&
          Array.isArray(s.puntos) &&
          s.puntos.length <= LIMITES_ESPECIFICACION.puntosPorSerieCurva &&
          s.puntos.every((p) => esObjeto(p) && finito(p.x) && finito(p.y)),
      ),
  );
}

function recursoDeTipo(v: unknown, tipo: string): boolean {
  return esObjeto(v) && v.resourceType === tipo;
}

const QR_TOKEN = /^[0-9a-f]{32,64}$/;

/** `null` si no tiene la forma de ninguna especificación conocida. */
export function validarEspecificacionPdf(v: unknown): EspecificacionPdf | null {
  if (!esObjeto(v) || !dentroDeLimites(v)) return null;
  switch (v.modo) {
    case 'questionnaire':
      return recursoDeTipo(v.questionnaire, 'Questionnaire') && recursoDeTipo(v.response, 'QuestionnaireResponse') && pacienteValido(v.paciente) && esTextoOpcional(v.fuenteFormulario)
        ? (v as unknown as EspecificacionPdf)
        : null;
    case 'pares':
      return esTexto(v.titulo) && paresValidos(v.pares) && pacienteValido(v.paciente) && curvasCrecimientoValidas(v.curvasCrecimiento) && esTextoOpcional(v.fuenteFormulario) ? (v as unknown as EspecificacionPdf) : null;
    case 'msp': {
      const e = v.datosEncabezadoMsp;
      const encabezadoOk =
        esObjeto(e) && esTexto(e.institucion) && esTexto(e.hoja) && esTexto(e.pacienteNombre) && esTexto(e.especialidad) && esTexto(e.codigo) && esTexto(e.tituloFormulario) && (e.pacienteIdentificacion === undefined || esTexto(e.pacienteIdentificacion));
      return encabezadoOk &&
        paresValidos(v.pares) &&
        diagramaOdontogramaValido(v.diagramaOdontograma) &&
        diagramaDenverValido(v.diagramaDenver) &&
        diagramaSignosVitalesValido(v.diagramaSignosVitales) &&
        diagramaPartogramaValido(v.diagramaPartograma) &&
        diagramasGraficoConstantesUciValidos(v.diagramasGraficoConstantesUci) &&
        siluetaCorporalValida(v.siluetaCorporal) &&
        curvasCrecimientoValidas(v.curvasCrecimiento)
        ? (v as unknown as EspecificacionPdf)
        : null;
    }
    case 'femo-expediente':
    case 'femo-certificado':
      return recursoDeTipo(v.response, 'QuestionnaireResponse') && pacienteValido(v.paciente) && typeof v.huboFirmaTrabajador === 'boolean'
        ? (v as unknown as EspecificacionPdf)
        : null;
    case 'receta': {
      const d = v.datos;
      const datosOk =
        esObjeto(d) &&
        esTexto(d.numeroReceta) &&
        esTexto(d.pacienteNombre) &&
        typeof d.sustanciaControlada === 'boolean' &&
        Array.isArray(d.medicamentos) &&
        d.medicamentos.length > 0 &&
        d.medicamentos.length <= LIMITES_ESPECIFICACION.medicamentos &&
        d.medicamentos.every((m) => esObjeto(m) && esTexto(m.nombreMedicamento));
      const tokenOk = v.qrToken === undefined || (esTexto(v.qrToken) && QR_TOKEN.test(v.qrToken));
      return datosOk && tokenOk ? (v as unknown as EspecificacionPdf) : null;
    }
    case 'acta-entrega':
      // Objeto nuevo a propósito: descarta cualquier `consultorio` del navegador.
      return { modo: 'acta-entrega' };
    default:
      return null;
  }
}
